import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useEffect, useState } from 'react';
import { TOAST } from 'constant';

import { setUnitPreQualificationDeadline } from 'apis/programs.api';
import { DatePicker, Select } from 'components/FormControl';
import { toast } from 'layout';
import { mapError } from 'utils/func';

function DeadlineItem({
  position,
  item,
  organizations,
  deadlines,
  programId,
  onChangeDeadline,
  onRemoveDeadline,
  onChangeSelectedDeadline,
  onChangeVisibleDeleteDialog,
  onChangeSavedDeadline,
}) {
  const { t } = useTranslation();
  const [validOrganizations, setValidOrganizations] = useState([]);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      ...item,
      deadline: item.deadline ? new Date(item.deadline) : null,
    },
    mode: 'onChange',
  });

  const { mutateAsync } = useMutation(setUnitPreQualificationDeadline);
  const organizationName = watch('organization.name');

  const handleChangeDeadline = async (value) => {
    const { deadline, organizationId } = value;
    const data = {
      deadline,
      organizationId,
    };

    try {
      const res = await mutateAsync({
        programId,
        data,
      });

      if (res.status === 200) {
        toast(TOAST.SUCCESS, t('program.deadline.updateSuccess'));
        reset({
          ...data,
          organization: {
            name: organizations.find((organization) => organization.id === organizationId)?.name,
          },
        });
        onChangeDeadline(position, organizationId, deadline);

        setValidOrganizations(() => {
          const filteredOrganizations = organizations.filter(
            (organization) => !deadlines.find((d) => organization.id === d.organizationId)
          );

          return filteredOrganizations;
        });
      } else {
        const error = res.data;
        toast(TOAST.ERROR, mapError(t, error));
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  useEffect(() => {
    setValidOrganizations(() => {
      const filteredOrganizations = organizations.filter(
        (organization) => !deadlines.find((d) => organization.id === d.organizationId)
      );

      return filteredOrganizations;
    });
  }, [deadlines, organizations]);

  useEffect(() => {
    if (isDirty) {
      onChangeSavedDeadline(position, false);
    }
  }, [isDirty]);

  return (
    <tr className="p-fluid">
      <td className="text-center">{position + 1}</td>
      <td style={{ maxWidth: '200px', width: '200px' }}>
        {organizationName ?? (
          <Select
            name="organizationId"
            control={control}
            errors={errors}
            options={validOrganizations}
            inputField="id"
          />
        )}
      </td>
      <td className="text-center w-8rem">
        <DatePicker name="deadline" control={control} errors={errors} />
      </td>
      <td style={{ maxWidth: '100px' }} className="text-center">
        <Button
          icon="pi pi-save"
          className="bg-blue-700 border-blue-700 hover:bg-blue-800"
          style={{ marginBottom: '14px' }}
          type="button"
          onClick={handleSubmit(handleChangeDeadline)}
          disabled={!isDirty}
          tooltip={t('program.save')}
        />
        <Button
          icon="pi pi-trash"
          severity="danger"
          className="ml-3"
          style={{ marginBottom: '14px' }}
          type="button"
          tooltip={t('program.deadline.delete')}
          onClick={() => {
            if (item.saved) {
              onChangeVisibleDeleteDialog();
              onChangeSelectedDeadline({ position, data: getValues() });
            } else {
              onRemoveDeadline(position, { saved: item.saved });
            }
          }}
        />
      </td>
    </tr>
  );
}

DeadlineItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  deadlines: PropTypes.array.isRequired,
  position: PropTypes.number.isRequired,
  programId: PropTypes.string.isRequired,
  onChangeDeadline: PropTypes.func.isRequired,
  onRemoveDeadline: PropTypes.func.isRequired,
  onChangeSelectedDeadline: PropTypes.func.isRequired,
  onChangeVisibleDeleteDialog: PropTypes.func.isRequired,
  onChangeSavedDeadline: PropTypes.func.isRequired,
};

export default DeadlineItem;
